import {
  BriefcaseBusiness,
  Fingerprint,
  Layers,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

import type { useTranslation } from 'react-i18next';
import type { SidebarGroup, SidebarItem } from '@/components/shell/Sidebar';

type Translator = ReturnType<typeof useTranslation>['t'];

export type TenantSectionGroup = 'tenant' | 'access' | 'operations';

export interface TenantSectionDescriptor {
  /** DOM-less section key: the URL hash anchor AND the switch key. */
  readonly id: string;
  readonly group: TenantSectionGroup;
  readonly labelKey: string;
  readonly Icon: LucideIcon;
}

/**
 * Section registry for the tenant record page (PTV2-1383). Mirrors the
 * patient record's `PATIENT_RECORD_SECTIONS`: a single source of truth that
 * the contextual sidebar derives its nav from, while `TenantRecordView`
 * (exported as `TenantDetailView`) switches the body on the URL hash.
 *
 * Unlike the patient sections, the bodies aren't self-contained render
 * closures — the wipe/apply-template job state is owned by the view — so the
 * registry carries only nav metadata and the view holds the `switch`.
 */
export const TENANT_SECTIONS: readonly TenantSectionDescriptor[] = [
  { id: 'overview', group: 'tenant', labelKey: 'routes.platform.detail.sidebar.overview', Icon: LayoutDashboard },
  { id: 'settings', group: 'tenant', labelKey: 'routes.platform.detail.sidebar.settings', Icon: Settings },
  { id: 'users', group: 'access', labelKey: 'routes.platform.detail.sidebar.users', Icon: Users },
  { id: 'roles', group: 'access', labelKey: 'routes.platform.detail.sidebar.roles', Icon: ShieldCheck },
  {
    id: 'federated-identities',
    group: 'access',
    labelKey: 'routes.platform.detail.sidebar.federatedIdentities',
    Icon: Fingerprint,
  },
  { id: 'overrides', group: 'operations', labelKey: 'routes.platform.detail.sidebar.overrides', Icon: Layers },
  { id: 'jobs', group: 'operations', labelKey: 'routes.platform.detail.sidebar.jobs', Icon: BriefcaseBusiness },
];

/** The first section is the implicit default when the URL carries no hash. */
export const TENANT_DEFAULT_SECTION_ID = TENANT_SECTIONS[0]?.id ?? 'overview';

const GROUP_ORDER: readonly TenantSectionGroup[] = ['tenant', 'access', 'operations'];

const GROUP_HEADING_KEY: Record<TenantSectionGroup, string> = {
  tenant: 'routes.platform.detail.sidebar.group.tenant',
  access: 'routes.platform.detail.sidebar.group.access',
  operations: 'routes.platform.detail.sidebar.group.operations',
};

/**
 * Contextual sidebar for a single tenant, derived from `TENANT_SECTIONS`.
 * The Image-overrides section is hidden (like its old tab) unless the tenant
 * actually carries overrides or the deployment manager is reachable.
 */
export function tenantSidebarGroups(
  uuid: string,
  t: Translator,
  opts: { readonly showOverrides: boolean },
): readonly SidebarGroup[] {
  const base = `/platform/tenants/${uuid}`;

  return GROUP_ORDER.map<SidebarGroup>((group) => {
    const items = TENANT_SECTIONS.filter(
      (section) =>
        section.group === group &&
        (section.id !== 'overrides' || opts.showOverrides),
    ).map<SidebarItem>((section) => ({
      key: section.id,
      label: t(section.labelKey),
      to: `${base}#${section.id}`,
      icon: <section.Icon className="h-4 w-4" aria-hidden="true" />,
      ...(section.id === TENANT_DEFAULT_SECTION_ID ? { defaultActive: true } : {}),
    }));

    return {
      key: group,
      heading: t(GROUP_HEADING_KEY[group]),
      items,
    };
  }).filter((g) => g.items.length > 0);
}
